import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Trophy, Medal, Award } from "lucide-react";

const PRIZE_TIERS = [
  { rank: 1, amount: 1500, label: "1st place", chip: "bg-amber-400 text-amber-950 border-amber-500", card: "bg-amber-400/95 text-amber-950 border-amber-500" },
  { rank: 2, amount: 1000, label: "2nd place", chip: "bg-slate-200 text-slate-800 border-slate-300", card: "bg-slate-200/90 text-slate-800 border-slate-300" },
  { rank: 3, amount: 750,  label: "3rd place", chip: "bg-orange-100 text-orange-900 border-orange-300", card: "bg-orange-100 text-orange-900 border-orange-300" },
  { rank: 4, amount: 500,  label: "4th place", chip: "bg-stone-200 text-stone-700 border-stone-300", card: "bg-stone-200 text-stone-700 border-stone-300" },
  { rank: 5, amount: 250,  label: "5th place", chip: "bg-stone-100 text-stone-600 border-stone-200", card: "bg-stone-100 text-stone-600 border-stone-200" },
];

export default function Leaderboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setRows(data)).finally(() => setLoading(false));
  }, []);

  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div className="min-h-screen">
      <Navbar />

      <main className="max-w-screen-2xl mx-auto px-6 md:px-10 pt-10 pb-20 relative z-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-lavender mb-3">The Standings</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight uppercase leading-none">
            The <span className="text-purple">Leaderboard</span>
          </h1>
          <p className="text-white/60 mt-3 text-sm">Match + Bonus points combined. Real names shown for prize distribution.</p>
        </motion.div>

        {/* PRIZE POOL row */}
        <div className="mb-10" data-testid="prize-pool">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px flex-1 max-w-[120px] bg-gradient-to-r from-transparent to-gold/40" />
            <div className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.3em] uppercase font-bold">
              <Trophy size={14}/> Prize Pool
            </div>
            <div className="h-px flex-1 max-w-[120px] bg-gradient-to-l from-transparent to-gold/40" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-3xl mx-auto">
            {PRIZE_TIERS.map((p) => (
              <div
                key={p.rank}
                data-testid={`prize-tier-${p.rank}`}
                className={`rounded-2xl border-2 px-4 py-4 text-center shadow-[0_4px_18px_rgba(0,0,0,0.25)] ${p.card}`}
              >
                <div className="text-[10px] tracking-widest uppercase opacity-70 font-semibold">{p.label}</div>
                <div className="text-[10px] tracking-widest uppercase opacity-60 mt-1">AED</div>
                <div className="font-mono font-bold text-2xl leading-none mt-1">{p.amount.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center text-white/40 py-20 text-sm tracking-widest uppercase">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-white/40 py-20">No players yet.</div>
        ) : (
          <>
            {/* Podium */}
            {top3.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 items-stretch">
                {[1, 0, 2].map((idx) => {
                  const r = top3[idx];
                  if (!r) return <div key={idx} />;
                  return <PodiumCard key={r.user_id} row={r} place={idx + 1} highlight={r.user_id === user?.id} />;
                })}
              </div>
            )}

            {/* Standings list */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.04 } } }}
              className="flex flex-col gap-2 max-w-4xl mx-auto"
            >
              {rest.map((r) => (
                <motion.div
                  key={r.user_id}
                  variants={{ hidden: { opacity: 0, x: -8 }, show: { opacity: 1, x: 0 } }}
                  data-testid={`lb-row-${r.user_id}`}
                  className={`row-hover flex items-center gap-4 px-5 py-4 rounded-2xl bg-surface border ${
                    r.user_id === user?.id
                      ? "border-lavender bg-lavender/5 shadow-[0_0_20px_rgba(232,180,240,0.18)]"
                      : "border-white/5"
                  }`}
                >
                  <div className="w-8 text-center font-mono font-bold text-white/40 shrink-0">{r.rank}</div>
                  <div className="font-semibold truncate flex-1 min-w-0">
                    {r.user_name}
                    {r.user_id === user?.id && <span className="ml-2 text-[10px] tracking-widest text-lavender uppercase">You</span>}
                    {(r.predictions_count > 0 || r.bonus_points > 0) && (
                      <div className="text-[10px] tracking-widest uppercase text-white/40 mt-0.5">
                        {r.predictions_count} preds · {r.exact_count} exact{r.bonus_points > 0 ? ` · ${r.bonus_points} bonus` : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {r.prize_aed > 0 && (
                      <PrizeChip amount={r.prize_aed} rank={r.rank} />
                    )}
                    <div className="font-mono font-bold text-2xl tabular-nums">
                      {r.total_points}<span className="text-white/30 text-sm ml-1">pts</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
}

function PrizeChip({ amount, rank }) {
  const tier = PRIZE_TIERS.find((t) => t.rank === rank) || PRIZE_TIERS[PRIZE_TIERS.length - 1];
  return (
    <div className={`hidden sm:inline-block px-3 py-1.5 rounded-full border text-[11px] font-bold tracking-wider ${tier.chip}`}>
      AED {amount.toLocaleString()}
    </div>
  );
}

function PodiumCard({ row, place, highlight }) {
  const meta = {
    1: { Icon: Trophy, label: "Champion",  border: "border-gold/60", glow: "shadow-[0_0_36px_rgba(255,215,0,0.3)]", accent: "text-gold", iconWrap: "bg-gold/15 border-gold/40" },
    2: { Icon: Medal,  label: "Runner-up", border: "border-white/15", glow: "shadow-[0_0_22px_rgba(255,255,255,0.08)]", accent: "text-white/70", iconWrap: "bg-white/5 border-white/15" },
    3: { Icon: Award,  label: "Third",     border: "border-orange-400/40", glow: "shadow-[0_0_22px_rgba(251,146,60,0.15)]", accent: "text-orange-300", iconWrap: "bg-orange-400/10 border-orange-400/30" },
  }[place];
  const { Icon } = meta;
  const tier = PRIZE_TIERS.find((t) => t.rank === place);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: place * 0.15 }}
      data-testid={`podium-${place}`}
      className={`pop-hover bg-surface border-2 ${meta.border} ${meta.glow} rounded-3xl px-6 py-7 text-center relative ${highlight ? "ring-2 ring-lavender/60" : ""} ${place === 1 ? "sm:scale-105 sm:-mt-2" : ""}`}
    >
      <div className={`mx-auto mb-4 w-14 h-14 rounded-full grid place-items-center border ${meta.iconWrap} ${meta.accent}`}>
        <Icon size={24}/>
      </div>
      <div className={`font-display text-4xl uppercase ${meta.accent} leading-none`}>#{place}</div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mt-2">{meta.label}</div>
      <div className="mt-4 font-semibold text-lg truncate">{row.user_name}</div>
      <div className="font-mono text-xl mt-1 tabular-nums">{row.total_points}<span className="text-white/40 text-xs ml-1">pts</span></div>
      {tier && (
        <div className={`mt-5 mx-auto inline-block px-4 py-1.5 rounded-full border-2 text-xs font-bold tracking-wider ${tier.chip}`}>
          AED {tier.amount.toLocaleString()}
        </div>
      )}
    </motion.div>
  );
}
